<template>
  <div>
    <setup-message-bird :ref="setupMessageBirdModalRef" :status-webhook-url="messageBirdStatusWebhookUrl"
                        :receive-webhook-url="messageBirdReceiveWebhookUrl"/>
    <mobile-test-connection :ref="testConnectionModalRef" :channel-id="channel.id"/>
    <channel-detail-field class="mb-3">
      <div slot="label">Provider:</div>
      <!-- provider name should be generated dynamically from channel.provider.type -->
      <div slot="value"><img width="24px" class="rounded m-0" :src="messageBirdLogo" alt="MessageBird"/>MessageBird
      </div>
    </channel-detail-field>
    <channel-detail-field v-if="!editMode" class="mb-3">
      <div slot="label">
        Sender ID:
        <span id="sender-id-info-show" v-b-popover:sender-id-info-show.html.hover.right="senderIdPopover">
          <icon name="info-circle"></icon>
        </span>
      </div>
      <div slot="value">{{ channel.provider.sender_id }}</div>
    </channel-detail-field>
    <div v-else class="row form-group">
      <label for="messagebird-sender-id" class="col-form-label col-4 col-xl-3 text-muted">
        Sender ID:
        <span id="sender-id-info-edit" v-b-popover:sender-id-info-edit.html.hover.right="senderIdPopover">
          <icon name="info-circle"></icon>
        </span>
      </label>

      <div class="col-8 col-xl-9">
        <input type="text"
               id="messagebird-sender-id"
               class="form-control"
               v-model="editableData.provider.sender_id">
      </div>
    </div>
    <channel-detail-field v-if="!editMode" class="mb-3 align-items-center">
      <div slot="label">Access Key:</div>
      <div slot="value">
        <sensitive-field :text="channel.provider.access_token" readonly/>
      </div>
    </channel-detail-field>
    <div v-else class="row form-group">
      <label for="messagebird-access-key" class="col-form-label col-4 col-xl-3 text-muted">Access Key:</label>
      <div class="col-8 col-xl-9">
        <input type="text"
               id="messagebird-access-key"
               class="form-control"
               v-model="editableData.provider.access_token">
      </div>
    </div>

    <channel-detail-field v-if="!editMode" class="mb-3 align-items-center">
      <div slot="label">Signing Key:</div>
      <div slot="value">
        <sensitive-field :text="channel.provider.signing_key" readonly/>
      </div>
    </channel-detail-field>
    <div v-else class="row form-group">
      <label for="messagebird-signing-key" class="col-form-label col-4 col-xl-3 text-muted">Signing Key:</label>
      <div class="col-8 col-xl-9">
        <input type="text"
               id="messagebird-signing-key"
               class="form-control"
               v-model="editableData.provider.signing_key">
      </div>
    </div>

    <div v-if="!editMode">
      <a href="#" role="button" class="btn btn-sm btn-primary mr-3" @click="showSetupMessageBird">
          <icon class="btn-icon" name="cog" scale="0.9"/>
          Setup MessageBird
      </a>
      <a href="#" role="button" class="btn btn-sm btn-success" @click="showTestConnection">
          <icon class="btn-icon" name="envelope" scale="0.9"/>
          Test Connection
      </a>
    </div>

  </div>
</template>

<script>
  import ChannelDetailField from '@/components/administration/common/Field'
  import CopyButton from '@/components/common/CopyButton'
  import SetupMessageBird from './SetupMessageBird'
  import MobileTestConnection from './MobileTestConnection'
  import messageBirdLogo from '@/assets/img/logo/channels/mobile/messagebird.svg'
  import 'vue-awesome/icons/info-circle'
  import 'vue-awesome/icons/cog'
  import 'vue-awesome/icons/envelope'
  import SensitiveField from '@/components/common/SensitiveField'

  const senderIdPopover = '<a href="https://support.messagebird.com/hc/en-us/articles/115002628665-What-is-the-Originator-" target="_blank">Originator</a> of sent messages. ' +
    'Please note that some countries apply <a href="https://support.messagebird.com/hc/en-us/sections/202017565-Country-Info-Restrictions" target="_blank">restrictions</a> and may overwrite this.'

  export default {
    components: {
      SensitiveField,
      MobileTestConnection,
      SetupMessageBird,
      CopyButton,
      ChannelDetailField
    },

    store: {
      channel: 'administration.channelDetail.channel',
      editableData: 'administration.channelDetail.channelEdit.editableData'
    },

    props: [
      'editMode'
    ],

    data() {
      return {
        messageBirdLogo,
        senderIdPopover,
        setupMessageBirdModalRef: 'setup-message-bird',
        testConnectionModalRef: 'test-connection'
      }
    },

    watch: {
      editMode: function () {
        if (this.editMode) {
          this.prepareStore()
        } else {
          this.cleanUpStore()
        }
      }
    },

    computed: {
      messageBirdStatusWebhookUrl() {
        return this.channel.provider.status_webhook_url
      },
      messageBirdReceiveWebhookUrl() {
        return this.channel.provider.receive_webhook_url
      }
    },

    methods: {
      onCopy() {
        const ref = this.$refs['webhook-url']
        let range, selection

        if (window.getSelection && document.createRange) {
          selection = window.getSelection()
          range = document.createRange()
          range.selectNodeContents(ref)
          selection.removeAllRanges()
          selection.addRange(range)
        } else if (document.selection && document.body.createTextRange) {
          range = document.body.createTextRange()
          range.moveToElementText(ref)
          range.select()
        }
      },

      showSetupMessageBird() {
        this.$refs[this.setupMessageBirdModalRef].show()
      },

      showTestConnection() {
        this.$refs[this.testConnectionModalRef].show()
      },

      prepareStore() {
        // load data to store
        this.editableData = {
          provider: {
            access_token: this.channel.provider.access_token,
            sender_id: this.channel.provider.sender_id,
            signing_key: this.channel.provider.signing_key
          }
        }
      },

      cleanUpStore() {
        this.editableData = {}
      }
    },

    mounted() {
      // show setup
      if (this.$route.query.showSetup === 'true') {
        this.showSetupMessageBird()
      }
    },

    destroyed() {
      this.cleanUpStore()

      const updateModePopupEvent = this.$enums.alerts.events.updateModeView
      const docLink = 'https://docs.amio.io/v1.0/reference#mobile-messages-text'
      this.$emit(updateModePopupEvent, docLink)
    }
  }
</script>
